import { BRAND_CONFIG_TIMEOUT_MS, fetchBrandJson } from './readBrandJson'

// A shared host serves several brands of one app and publishes which ones (Specs > Shared domains).
export const BRANDS_LIST_PATH = '/brand/brands.json'

export interface SharedBrand {
  slug: string
  name: string
  market?: string
}

export const brandConfigPath = (slug: string): string =>
  `/brand/${encodeURIComponent(slug)}/config.json`

// Lowercase slugs only, so a slug can never leave /brand/ ('..' survives URL encoding).
const SLUG = /^[a-z0-9-]+$/

const isSharedBrand = (value: unknown): value is SharedBrand =>
  typeof (value as SharedBrand)?.slug === 'string' &&
  SLUG.test((value as SharedBrand).slug) &&
  typeof (value as SharedBrand)?.name === 'string'

export type SharedBrandsResult =
  | { kind: 'shared'; brands: SharedBrand[] }
  // No list: a pinned host.
  | { kind: 'absent' }
  // A list that cannot be read or used; booting anything else could pick the wrong tenant.
  | { kind: 'failed' }

export const loadSharedBrands = async (
  timeoutMs = BRAND_CONFIG_TIMEOUT_MS
): Promise<SharedBrandsResult> => {
  const result = await fetchBrandJson(BRANDS_LIST_PATH, timeoutMs)
  if (result.kind !== 'json') return result

  const brands = (result.value as { brands?: unknown } | null)?.brands
  if (!Array.isArray(brands) || brands.length === 0 || !brands.every(isSharedBrand)) {
    return { kind: 'failed' }
  }
  return {
    kind: 'shared',
    brands: brands.map(({ slug, name, market }) => ({
      slug,
      name,
      ...(typeof market === 'string' ? { market } : {})
    }))
  }
}

// localStorage is per origin, so the pick is already per host.
const SAVED_BRAND_KEY = 'brand.selected'

export const readSavedBrand = (): string | null => {
  try {
    return localStorage.getItem(SAVED_BRAND_KEY)
  } catch (error) {
    console.warn('Saved brand could not be read; booting the first listed brand:', error)
    return null
  }
}

export const saveBrand = (slug: string): void => {
  try {
    localStorage.setItem(SAVED_BRAND_KEY, slug)
  } catch (error) {
    // No storage: every load boots the first listed brand instead.
    console.warn('Brand pick could not be saved; every load boots the first listed brand:', error)
  }
}

export const clearSavedBrand = (): void => {
  try {
    localStorage.removeItem(SAVED_BRAND_KEY)
  } catch (error) {
    console.warn('Saved brand could not be cleared:', error)
  }
}

// Boot reads the saved pick, so reloading lands on the new brand.
export const switchBrand = (slug: string): void => {
  saveBrand(slug)
  location.reload()
}
